// TD-WEBAPP-001: tipos *Local reemplazables por openapi-generated. Ver TECH_DEBT.md.
/**
 * Tipos LOCALES de los endpoints `/platform/tenants/*` (Platform module, Fase 1).
 *
 * A diferencia de `TenantWire.ts` (que hand-rollea shapes porque el rbac runtime de
 * `/tenants/:tid/*` diverge del openapi), aquí los WIRE shapes SÍ coinciden con el contrato
 * generado, así que los derivamos directo de `backoffice.gen.ts` vía `components['schemas']`
 * (mismo mecanismo que `permissions.ts`). Los *Local difieren del wire en un solo punto:
 * los timestamps epoch ms (number) se normalizan a ISO string para el render.
 */
import type { components } from '../generated/backoffice.gen';

/* ─── Wire (alias del contrato generado) ─── */

export type CreateTenantRequestWire = components['schemas']['CreateTenantRequest'];
export type CreateTenantResponseWire = components['schemas']['CreateTenantResponse'];
export type TenantDetailWire = components['schemas']['TenantDetail'];
export type UpdateTenantRequestWire = components['schemas']['UpdateTenantRequest'];
export type TenantCognitoPoolWire = components['schemas']['TenantCognitoPool'];
export type TenantSecurityPolicyWire = components['schemas']['TenantSecurityPolicy'];
export type TenantBrandingConfigWire = components['schemas']['TenantBrandingConfig'];

/**
 * Estado del tenant tal cual lo devuelve el backend.
 *
 * TODO: reemplazar por tipo generado tras regen openapi. El backend de "pendiente de infra"
 * (DEC-RBAC-058) AÚN NO está desplegado → el `.gen` todavía no trae `'pending-infra'` en el enum de
 * status. Lo sumamos local-only aquí hasta que `npm run sync-api-types` lo incorpore; entonces volver a
 * `= TenantDetailWire['status']` a secas.
 */
export type PlatformTenantStatusLocal = TenantDetailWire['status'] | 'pending-infra';

/**
 * DEC-RBAC-002: modo de validación de los tokens del tenant. Lo expone `TenantDetail` y lo acepta
 * `UpdateTenantRequest`/`CreateTenantRequest`. Es OPCIONAL en el contrato; cuando viene ausente la
 * UI asume `'offline'` (el default del backend).
 */
export type TokenValidationModeLocal = NonNullable<TenantDetailWire['tokenValidationMode']>;

/** Default cuando el backend no devuelve el modo (campo opcional en el contrato). */
export const DEFAULT_TOKEN_VALIDATION_MODE: TokenValidationModeLocal = 'offline';

/**
 * DEC-RBAC-027: niveles en los que el tenant trae su PROPIO id (BYOID — bring your own id) en vez de un
 * UUID generado. El backend lo modela como `externalScopeLevels`: un array del enum CANÓNICO
 * `PermissionScope`, que tras la ampliación del contrato (proyecto niveles genéricos) incluye tanto los
 * niveles legacy semánticos (`GROUP | RETAILER | STORE`) como los genéricos (`LEVEL_1 | LEVEL_2 | LEVEL_3`)
 * — además de `PLATFORM`/`TENANT`, que NO son niveles BYOID válidos (el control nunca los ofrece). Lo
 * derivamos del contrato generado (`externalScopeLevels` de TenantDetail) para que `tsc` lo mantenga en
 * sync con el backend; ya NO es un subset restringido a los 3 legacy.
 *
 * Semántica: array vacío (o ausente) = BYOID apagado para ese tenant. Uno o más niveles = prendido en
 * esos niveles. Qué niveles ofrece el control sale SIEMPRE de los `levels` de la APP del tenant (ya no
 * del `tenantType` legacy): `byoidOptionsFromLevels(...)` los deriva 1:1.
 */
export type ExternalScopeLevelLocal = NonNullable<TenantDetailWire['externalScopeLevels']>[number];

/**
 * Niveles de RECURSO del tenant, derivados de la APLICACIÓN del tenant (proyecto "aplicaciones/niveles
 * genéricos"). El backend ahora expone en `TenantDetail` el `applicationId` + un array `levels` donde cada
 * entrada es `{ level, displayName }`: `level` es la key del nivel (`LEVEL_1/2/3` para apps nuevas; o los
 * legacy semánticos `RETAILER/STORE/GROUP`) y `displayName` es el nombre de negocio que la APP definió para
 * ese nivel (ej. "Sucursal", "Región"). Reemplaza el viejo cableado por `tenantType` (Retailers/Tiendas/
 * Grupos hardcodeados): los tabs de recurso del drill-down salen 1:1 de este array.
 */
export type TenantLevelLocal = NonNullable<TenantDetailWire['levels']>[number];

/** Meta de cada modo: label legible (es) + descripción de su comportamiento. */
export const TOKEN_VALIDATION_MODE_META: Record<
    TokenValidationModeLocal,
    { label: string; description: string }
> = {
    offline: {
        label: 'Sin conexión',
        description: 'Valida solo la firma (rápido, sin consultar al servicio de identidad).',
    },
    online_preferred: {
        label: 'En línea preferido',
        description:
            'Consulta al servicio de identidad; si no responde, sigue funcionando y valida solo la firma.',
    },
    online_strict: {
        label: 'En línea estricto',
        description:
            'Consulta al servicio de identidad en cada solicitud; si no responde, NIEGA el acceso.',
    },
};

/* ─── List item ─── */

/**
 * Fila del listado de tenants. El list (`GET /platform/tenants`) devuelve `TenantDetail[]`
 * completo, pero la UI del listado sólo consume estos campos; el detalle completo se trae con
 * `getTenant`. `createdAt`/`updatedAt` ya normalizados a ISO string.
 */
export interface PlatformTenantListItemLocal {
    tenantId: string;
    displayName: string;
    status: PlatformTenantStatusLocal;
    tenantType?: string;
    /** DEC-RBAC-002: modo de validación de tokens (el list devuelve `TenantDetail` completo → lo trae). */
    tokenValidationMode?: TokenValidationModeLocal;
    /** DEC-RBAC-027: niveles BYOID prendidos para el tenant (el list devuelve `TenantDetail` completo → lo trae). */
    externalScopeLevels?: ExternalScopeLevelLocal[];
    /** Aplicación del tenant (proyecto niveles genéricos). El list devuelve `TenantDetail` completo → lo trae. */
    applicationId?: string;
    /** Niveles de recurso de la APP del tenant ({ level, displayName }). El list devuelve `TenantDetail` completo → lo trae. */
    levels?: TenantLevelLocal[];
    createdAt: string;
    updatedAt: string;
}

/* ─── Detail ─── */

export interface PlatformTenantCognitoPoolLocal {
    userPoolId: string;
    userPoolArn: string;
    region: string;
    appClientId: string;
    displayName: string;
    /** Normalizado epoch ms → ISO string. */
    createdAt: string;
}

/**
 * DEC-RBAC-079/081: identificador de login del tenant. `principalIdentifier` = con qué dato
 * inician sesión sus usuarios (email o teléfono); `allowSecondary` = si se permite un
 * identificador secundario verificable. Ambos inmutables, heredados del app al onboardear.
 * Derivados del contrato generado (`TenantDetail`).
 */
export type PrincipalIdentifierLocal = NonNullable<TenantDetailWire['principalIdentifier']>;

export interface PlatformTenantDetailLocal {
    tenantId: string;
    displayName: string;
    tablePrefix: string;
    status: PlatformTenantStatusLocal;
    /**
     * DEC-RBAC-079/081: identificador primario de login del tenant. Puede venir `undefined` en
     * tenants viejos (pre-feature) → el consumidor asume `'EMAIL'` (back-compat: email-only).
     */
    principalIdentifier?: PrincipalIdentifierLocal;
    /**
     * DEC-RBAC-079/081: si el tenant permite un identificador secundario opcional verificable.
     * `undefined` en tenants viejos → el consumidor asume `false`.
     */
    allowSecondary?: boolean;
    scope?: TenantDetailWire['scope'];
    legalInfo?: Record<string, unknown>;
    cognitoPool: PlatformTenantCognitoPoolLocal;
    securityPolicy?: TenantSecurityPolicyWire;
    brandingConfig?: TenantBrandingConfigWire;
    featureFlags?: Record<string, boolean>;
    connectorArns?: Record<string, string>;
    s3Bucket?: string;
    iamRoleArn?: string;
    /** Normalizado epoch ms → ISO string. */
    createdAt: string;
    /** Normalizado epoch ms → ISO string. */
    updatedAt: string;
    createdBy?: string;
    updatedBy?: string;
    tenantType?: string;
    /** DEC-RBAC-027: niveles BYOID prendidos para el tenant. */
    externalScopeLevels?: ExternalScopeLevelLocal[];
    /** Aplicación del tenant (proyecto niveles genéricos). Fuente del array `levels`. */
    applicationId?: string;
    /**
     * Niveles de recurso de la APP del tenant ({ level, displayName }). Fuente de verdad de los tabs de
     * recurso del drill-down (reemplaza el cableado por `tenantType`). Ausente/vacío → tenant sin recursos
     * de nivel (ej. una app de 0 niveles) → 0 tabs de recurso.
     */
    levels?: TenantLevelLocal[];
    /**
     * TODO: reemplazar por tipo generado tras regen openapi. DEC-RBAC-058: nombres de las tablas DDB del
     * silo que faltan crear. Presente SOLO cuando `status === 'pending-infra'`; al pasar a `active` el
     * backend lo limpia. Local-only hasta que `sync-api-types` lo agregue a `TenantDetail`.
     */
    infraRequirements?: string[];
}

/* ─── Create ─── */

/**
 * Tipo de tenant. Define el TECHO de permisos del tenant (catálogo del tipo). El backend lo modela como
 * enum `@IsEnum(TenantType)` opcional en `CreateTenantRequest` (default `retail` si no se envía).
 *
 * Hand-rolleado como union: hoy el `.gen` AÚN no expone `tenantType` en `CreateTenantRequest` (el sync
 * de openapi lo agrega en paralelo — TD-WEBAPP-001). Cuando llegue, derivar de `components['schemas']`.
 */
export type TenantTypeLocal = 'retail' | 'agents';

/** Default del backend cuando el alta no envía `tenantType` (back-compat). */
export const DEFAULT_TENANT_TYPE: TenantTypeLocal = 'retail';

/** Opciones del Select del alta: label legible (es) por cada tipo de tenant. */
export const TENANT_TYPE_OPTIONS: { value: TenantTypeLocal; label: string }[] = [
    { value: 'retail', label: 'Retail' },
    { value: 'agents', label: 'Agentes' },
];

/**
 * Opción del editor BYOID, derivada de los `levels` de la APP del tenant. El editor no deriva el label
 * por i18n del enum: lo recibe explícito aquí, así muestra el `displayName` real que la APP definió para
 * cada nivel (ej. "Grupo", "Sucursal").
 *
 * `byoidValue`: el valor del enum `PermissionScope` (`ExternalScopeLevelLocal`) que se persiste en
 *   `externalScopeLevels`. Tras la ampliación del contrato (el enum ahora incluye `LEVEL_1/2/3`), TODO nivel
 *   de la app — legacy (`GROUP/RETAILER/STORE`) o genérico (`LEVEL_N`) — es persistible 1:1, así que para
 *   las opciones que arma `byoidOptionsFromLevels` siempre viene definido. Se mantiene opcional como guardia
 *   defensiva del editor.
 */
export interface ByoidLevelOption {
    /** Key de presentación/render (estable). Para niveles legacy = el enum; para genéricos = el level key. */
    key: string;
    /** Label legible a mostrar (displayName de la app, o el label i18n del nivel legacy). */
    label: string;
    /** Valor persistible en `externalScopeLevels`; `undefined` = nivel sin soporte BYOID en el contrato. */
    byoidValue?: ExternalScopeLevelLocal;
}

/**
 * Construye las opciones del editor BYOID a partir de los `levels` de la APP del tenant (proyecto niveles
 * genéricos). Cada nivel de la app se muestra por su `displayName`. Tras la ampliación del contrato
 * (`externalScopeLevels: PermissionScope[]` ahora incluye `LEVEL_1/2/3`), TODO `level` de la app es
 * persistible 1:1 — tanto los legacy (`GROUP/RETAILER/STORE`) como los genéricos (`LEVEL_N`) — así que
 * `byoidValue` = la propia key del nivel. Las keys que la app define (`level`) son un subconjunto del enum
 * `PermissionScope`, del que se deriva `ExternalScopeLevelLocal`, así que la asignación es directa (sin cast).
 */
export function byoidOptionsFromLevels(levels: TenantLevelLocal[]): ByoidLevelOption[] {
    return levels.map((l) => ({
        key: l.level,
        label: l.displayName,
        byoidValue: l.level,
    }));
}

/**
 * Request de `POST /platform/tenants`. Espejo del contrato generado MENOS `emailDomain`: ese campo se
 * retiró del modelo (DEC-060 — el login es por membresía, no por dominio de email). El backend lo está
 * barriendo en paralelo; mientras tanto el `.gen` aún lo trae como required, así que lo restamos aquí con
 * `Omit` (no se hand-editea el generado). Cuando el sync de openapi lo quite, este `Omit` queda inocuo y
 * se puede simplificar a `= CreateTenantRequestWire`.
 *
 * `tenantType` se suma local-only hasta que el sync de openapi lo agregue a `CreateTenantRequest`
 * (TD-WEBAPP-001). Opcional: si no se envía, el backend usa el default `retail`. El backend YA lo acepta
 * (DTO `CreateTenantRequest` en `@fiado/type-kit` 3.74.0). Cuando el sync llegue, quitar este `&`.
 */
export type CreateTenantRequestLocal = Omit<CreateTenantRequestWire, 'emailDomain'> & {
    tenantType?: TenantTypeLocal;
};

/**
 * Respuesta de `POST /platform/tenants`.
 *
 * TODO: reemplazar por tipo generado tras regen openapi. DEC-RBAC-057: el alta YA NO crea admin → la
 * respuesta perdió `adminCognitoSub`/`temporaryPassword`. DEC-RBAC-058: ahora trae `status` (que puede
 * ser `'pending-infra'`) y, en ese caso, la lista `infraRequirements` de tablas faltantes. Local-only
 * hasta que el backend esté en develop y `sync-api-types` regenere `CreateTenantResponse`.
 */
export interface CreateTenantResponseLocal {
    tenantId: string;
    userPoolId: string;
    appClientId: string;
    status: PlatformTenantStatusLocal;
    /** Presente solo cuando `status === 'pending-infra'`: nombres de las tablas DDB del silo a crear. */
    infraRequirements?: string[];
}

/**
 * Respuesta de `POST /platform/tenants/{tenantId}/verify-infra` (DEC-RBAC-059, re-chequeo idempotente).
 *
 * TODO: reemplazar por tipo generado tras regen openapi. Si las tablas ya existen → `status: 'active'`
 * (sin `infraRequirements`); si aún faltan → `status: 'pending-infra'` + la lista actualizada. Local-only
 * hasta que el backend esté en develop y `sync-api-types` lo genere.
 */
export interface VerifyTenantInfraResponseLocal {
    status: PlatformTenantStatusLocal;
    infraRequirements?: string[];
}

/* ─── Update ─── */

/** Request de `PUT /platform/tenants/{tenantId}`. Subset opcional mutable; espejo del contrato. */
export type UpdateTenantRequestLocal = UpdateTenantRequestWire;

/* ─── Roles PLATFORM ─── */
// Endpoints `/platform/roles[/{roleId}]`. A diferencia de los roles del tenant (TenantWire — el
// rbac runtime diverge), el contrato de roles platform SÍ coincide con el openapi generado, así que
// los derivamos directo de `components['schemas']`. El scope del rol se AUTOCALCULA en el backend
// (DEC-037: el más amplio de sus permisos) e `isSystem` se fuerza false al crear — por eso el
// Create no los pide.
//
// IMPORTANTE: el LIST (`GET /platform/roles`) devuelve `RoleListItem` (genérico: permissions opcional
// como string[], isSystem opcional, sin createdAt/updatedAt), NO el `PlatformRoleDetail` completo
// (que sí trae el create/update). Por eso la fila del listado se modela sobre `RoleListItem` y sus
// `permissions` se estrechan al union `Permission` para que el PermissionPicker (tipado a Permission[])
// los consuma sin `any`.
import type { Permission, PermissionCatalogItem } from './permissions';

/** Fila del listado de roles PLATFORM (`GET /platform/roles` → `RoleListItem`). */
export interface PlatformRoleListItemLocal {
    roleId: string;
    displayName: string;
    description?: string;
    scope: components['schemas']['PermissionScope'];
    isSystem?: boolean;
    permissions?: Permission[];
}

/** Respuesta (ya desenvuelta del envelope) de `GET /platform/roles`. */
export interface PlatformRoleListResponseLocal {
    items: PlatformRoleListItemLocal[];
}

/** Detalle de un rol PLATFORM (`PlatformRoleDetail` — lo devuelven create/update). */
export type PlatformRoleDetailLocal = components['schemas']['PlatformRoleDetail'];

/** Request de `POST /platform/roles`. El backend autocalcula `scope` y fuerza `isSystem=false`. */
export type CreatePlatformRoleRequestLocal = components['schemas']['CreatePlatformRoleRequest'];

/** Request de `PUT /platform/roles/{roleId}`. `roleId`/`scope`/`isSystem` no son editables. */
export type UpdatePlatformRoleRequestLocal = components['schemas']['UpdatePlatformRoleRequest'];

/* ─── Administradores PLATFORM (`/platform/users/*`) ─── */
// Endpoints `/platform/users[/{cognitoSub}]` + sus acciones (disable/enable) y los role-assignments
// (`/platform/users/{cognitoSub}/role-assignments`). El contrato de admins platform SÍ coincide con el
// openapi generado, así que derivamos los *Local directo de `components['schemas']` (mismo mecanismo que
// roles platform). IMPORTANTE: el alta del admin (`POST /platform/users`) NO lleva rol ni scope — la
// asignación de rol es un flujo aparte (`POST .../role-assignments`). El backend RECALCULA el effective
// scope desde el ROL (220f7b4): rol PLATFORM → scopeRef '*' (global); rol asignado por este path acotado
// a un tenant → scopeRef = tenantId.

/** Estado del admin platform (mismo union que el user del tenant). */
export type PlatformUserStatusLocal = components['schemas']['PlatformUserDetail']['status'];

/** Detalle de un admin platform (`GET /platform/users/{cognitoSub}`). Timestamps en epoch ms (number). */
export type PlatformUserDetailLocal = components['schemas']['PlatformUserDetail'];

/** Fila del listado de admins (`GET /platform/users` → `PlatformUserListItem` = `PlatformUserDetail`). */
export type PlatformUserListItemLocal = components['schemas']['PlatformUserListItem'];

/** Request de `POST /platform/users`. NO lleva rol ni scope (eso es flujo aparte). */
export type CreatePlatformUserRequestLocal = components['schemas']['CreatePlatformUserRequest'];

/** Respuesta de `POST /platform/users`. `temporaryPassword` viene solo si Cognito la generó (one-time). */
export type CreatePlatformUserResponseLocal = components['schemas']['CreatePlatformUserResponse'];

/** Request de `PUT /platform/users/{cognitoSub}`. Subset opcional mutable. */
export type UpdatePlatformUserRequestLocal = components['schemas']['UpdatePlatformUserRequest'];

/** Request de `POST /platform/users/{cognitoSub}/role-assignments`. `scopeRef`='*' (global) o tenantId. */
export type AssignPlatformRoleRequestLocal = components['schemas']['AssignPlatformRoleRequest'];

/** Un assignment de rol de un admin platform (`GET .../role-assignments`). `grantedAt` epoch ms (number). */
export type PlatformRoleAssignmentItemLocal = components['schemas']['PlatformRoleAssignmentItem'];

/** Respuesta (ya desenvuelta del envelope) de `GET .../role-assignments`. */
export interface PlatformRoleAssignmentListResponseLocal {
    items: PlatformRoleAssignmentItemLocal[];
}

/* ─── Tipos de tenant (`/platform/tenant-types/*`) ─── */
// TD-WEBAPP-001: tipos *Local reemplazables por openapi-generated cuando el backend publique sus yamls
// y se corra `npm run sync-api-types`. Hoy estos endpoints AÚN NO existen en `backoffice.gen.ts` (los
// implementa otro agente en paralelo), así que NO los derivamos de `components['schemas']` todavía —
// los hand-rolleamos contra el contrato acordado. Cuando el sync llegue: reemplazar por los alias del
// .gen y borrar estas interfaces.
//
// Un "tipo de tenant" (hoy solo `retail`) define el TECHO de permisos disponibles para TODOS los
// tenants de ese tipo (catálogo del tipo). Editar ese catálogo aplica de inmediato (hard ceiling).

/** Fila del listado de tipos de tenant (`GET /platform/tenant-types` → `items[]`). */
export interface TenantTypeInfoLocal {
    /** Identificador del tipo (ej. `retail`). */
    tenantType: string;
    /** Nombre legible para mostrar. */
    displayName: string;
    /** Sub-niveles del tipo (ej. `retailer`, `store`). */
    levels: string[];
    /** Cantidad de permisos en el catálogo (techo) del tipo. */
    permissionCount: number;
    /** Cantidad de tenants existentes de este tipo (impacto de un cambio de catálogo). */
    tenantCount: number;
}

/** Respuesta (ya desenvuelta del envelope) de `GET /platform/tenant-types`. */
export interface TenantTypeListResponseLocal {
    items: TenantTypeInfoLocal[];
}

/** Item del universo de permisos asignables a un tipo de tenant (mismo shape que el catálogo). */
export type PermissionCatalogItemLocal = PermissionCatalogItem;

/**
 * Catálogo de permisos de un tipo de tenant (`GET/PUT /platform/tenant-types/{tenantType}/catalog`).
 * `keys` son las PermissionKey del techo actual; se estrechan al union `Permission` (el backend solo
 * emite claves del enum del catálogo). `available` es el UNIVERSO de permisos asignables a un tipo de
 * tenant (todos los no-platform) — lo entrega el GET catalog para que el editor renderice los checkboxes
 * sin depender del PermissionPicker contextual (que devuelve el techo del actor, no el del tipo; DEC-073).
 * El PUT response NO trae `available` (lo dejamos opcional).
 */
export interface TenantTypeCatalogLocal {
    tenantType: string;
    keys: Permission[];
    available?: PermissionCatalogItemLocal[];
}

/** Request de `PUT /platform/tenant-types/{tenantType}/catalog`. */
export interface UpdateTenantTypeCatalogRequestLocal {
    keys: Permission[];
}
